import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import { Justify } from 'tea-component';
import { scrollable } from 'tea-component/lib/table/addons';

import RenderOperations from '@sg/components/Chart/Operations';
import Chart from '@sg/components/Chart';
import TableContent from '@sg/components/TableContent';
import Loading from '@intelligence/components/Loading';

import { pickOperations } from '@sg/commonData/pickOperations';
import { rendCardTitle } from '../../utils/rendCardTitle';
import { xAxisHighLight, applyValueAxisBoundaryGap } from '@sg/utils/chatUtils';

import type { TInDevChartViewKey } from '../../../../hooks/useInDevChartEyesState';
import {
  getDefaultHeadRateColumns,
  getInDevCardChartHeight,
  getInDevCardTableBodyMaxHeight,
  showFinancialsList,
} from '../../../../pageConfig';
import { useHeadcountRampData } from './hooks/useHeadcountRampData';

interface IHeadcountRampCardProps {
  projectId: string;
  activeDateTypeSegment: string;
  selectedMonth?: string | null;
  smallChartViewState: Record<TInDevChartViewKey, boolean>;
  updateInDevEyesViewState: (viewKey: TInDevChartViewKey, enabled: boolean) => void;
  projectInfo: string;
  isSpendTrendPhaseScrollable: boolean;
}

const HeadcountRampCard: React.FC<IHeadcountRampCardProps> = ({
  projectId,
  activeDateTypeSegment,
  selectedMonth,
  smallChartViewState,
  updateInDevEyesViewState,
  projectInfo,
  isSpendTrendPhaseScrollable,
}) => {
  const { formatMessage } = useIntl();

  const {
    tableData,
    chartData,
    updateTime,
    loading,
    showChatsType,
    handleChatsTypeChange,
  } = useHeadcountRampData({
    projectId,
    selectedMonth,
    activeDateTypeSegment,
    projectInfo,
    viewEnabled: !!smallChartViewState.headcountRamp,
    updateInDevEyesViewState,
  });

  const columns = getDefaultHeadRateColumns(activeDateTypeSegment, formatMessage);

  const chartHeight = useMemo(() => getInDevCardChartHeight(), []);
  const tableBodyMaxHeight = useMemo(
    () => getInDevCardTableBodyMaxHeight({ tableHeaderRows: 1 }),
    [],
  );

  return (
    <div className="dbs-by-date-card">
      <Justify
        bottom
        left={rendCardTitle({
          title: `Headcount Ramp`,
          tips: updateTime ? `${formatMessage({ id: 'dbs_update_on' })} ${updateTime}` : ''
        })}
        right={<RenderOperations
          key={`headramp-ops-${showChatsType.value}-${showChatsType.format}`}
          defaultValue={showChatsType.value}
          defaultValueShow={showChatsType.format}
          change={handleChatsTypeChange}
          options={pickOperations(['view', ...showFinancialsList, 'download'], formatMessage)}
          downloadParams={{
            data: tableData,
            columns,
            name: `Headcount Ramp`,
          }}
        >
        </RenderOperations>}
      />
      {loading && (
        <Loading style={{ height: chartHeight }} />
      )}
      {!loading && showChatsType?.value !== 'table' && chartData.seriesData &&
        <Chart
          data={chartData}
          key={`HeadRate${showChatsType?.value}${projectId}${activeDateTypeSegment}`}
          type={showChatsType?.value}
          height={chartHeight}
          numberType={'num'}
          setOtherConfigFunc={(opt: any) => {
            opt.grid.containLabel = true;
            opt.legend.show = true;
            opt.legend.formatter = (name: string) =>
              name === 'Total' ? 'Headcount' : name;
            if (activeDateTypeSegment === 'phase') {
              // Match spend trend x-axis behavior: trim long labels with "..."
              const primaryXAxis = Array.isArray(opt?.xAxis)
                ? opt.xAxis?.[0]
                : opt?.xAxis;
              if (primaryXAxis) {
                primaryXAxis.axisLabel = {
                  ...(primaryXAxis.axisLabel || {}),
                  margin: 8,
                  formatter: (label: string) => {
                    if (!label) {
                      return '';
                    }
                    return label.length > 30
                      ? `${label.slice(0, 30)}...`
                      : label;
                  },
                };
              }
            }
            if (showChatsType?.value === 'bar') {
              // Pad y-axis so bar/total labels are not clipped at the chart edge.
              applyValueAxisBoundaryGap(opt);
            }
            if (isSpendTrendPhaseScrollable) {
              // Keep spacing in sync with spend trend scrollbar state.
              opt.grid.bottom = 60;
            } else {
              // Reset to default bottom spacing when no phase scroll is needed.
              // opt.grid.bottom = 0;
            }
            opt = xAxisHighLight(opt, {
              type: "shadow",
            } as any);
            return opt;
          }}
        ></Chart>}
      {!loading && showChatsType?.value === 'table' && <div style={{ marginTop: 20 }}>
        <TableContent
          className="dbs-commons-table dbs-chats-table"
          emptyMinHeight={chartHeight}
          datas={tableData}
          columns={columns}
          sort={[{ by: 'fill_time', order: 'desc' }]}
          resultKey="list"
          recordKey="date"
          addons={[
            // 支持表格滚动，高度超过 192 开始显示滚动条
            scrollable({
              maxHeight: tableBodyMaxHeight,
              // maxHeight: '310px',
              // minWidth: headcountActive === 'headcount' ? 1400 : 'auto',
            }),
          ]}
        ></TableContent>

      </div>}
    </div>
  );
};

export default HeadcountRampCard;
